/**
 * seekableVoice — rend parcourable un vocal MP4 fragmenté sans index, avant que le lecteur d'Android
 * ne le lise.
 *
 * Un vocal enregistré par un navigateur (le chat du tuteur sur le web : `MediaRecorder`, en
 * `audio/mp4`) est un MP4 fragmenté : `ftyp`, `moov`, puis des paires `moof` + `mdat`, sans `sidx`
 * ni `mfra`. AVPlayer (iOS) y cherche quand même ; ExoPlayer (Android) le déclare non parcourable et
 * repart de 0:00 à chaque `seekTo` — le curseur revenait au début au lâcher. Avec un `sidx` posé
 * devant le premier fragment, ExoPlayer sait où commence chaque fragment et à quel instant.
 *
 * Pur : des octets entrent, des octets sortent, ou `null` quand il n'y a rien à faire (un MP4 non
 * fragmenté, comme l'enregistreur de l'app ; un index déjà là) ou rien de sûr à faire (plusieurs
 * pistes, un fragment qui adresse ses données en absolu et que l'insertion décalerait).
 */

type Box = { type: string; start: number; size: number; header: number }

const SIDX_SAP = 0x90000000 // commence par un point d'accès (SAP de type 1)

function readU32(bytes: Uint8Array, at: number): number {
    return ((bytes[at]! << 24) | (bytes[at + 1]! << 16) | (bytes[at + 2]! << 8) | bytes[at + 3]!) >>> 0
}

function readU64(bytes: Uint8Array, at: number): number {
    return readU32(bytes, at) * 2 ** 32 + readU32(bytes, at + 4)
}

function typeAt(bytes: Uint8Array, at: number): string {
    return String.fromCharCode(bytes[at]!, bytes[at + 1]!, bytes[at + 2]!, bytes[at + 3]!)
}

/** Les boîtes d'un intervalle, à plat. `null` si une taille sort de l'intervalle : le fichier n'est pas lisible ici. */
function boxesIn(bytes: Uint8Array, from: number, to: number): Box[] | null {
    const boxes: Box[] = []
    let at = from
    while (at + 8 <= to) {
        let size = readU32(bytes, at)
        let header = 8
        if (size === 1) {
            if (at + 16 > to) return null
            size = readU64(bytes, at + 8)
            header = 16
        } else if (size === 0) {
            size = to - at
        }
        if (size < header || at + size > to) return null
        boxes.push({ type: typeAt(bytes, at + 4), start: at, size, header })
        at += size
    }
    return boxes
}

function child(bytes: Uint8Array, parent: Box, type: string): Box | null {
    const inner = boxesIn(bytes, parent.start + parent.header, parent.start + parent.size)
    return inner?.find((box) => box.type === type) ?? null
}

function children(bytes: Uint8Array, parent: Box, type: string): Box[] {
    const inner = boxesIn(bytes, parent.start + parent.header, parent.start + parent.size)
    return inner?.filter((box) => box.type === type) ?? []
}

/** La piste unique du `moov` : son identifiant, son échelle de temps, la durée d'échantillon par défaut. */
function trackOf(bytes: Uint8Array, moov: Box): { id: number; timescale: number; defaultDuration: number } | null {
    const traks = children(bytes, moov, "trak")
    if (traks.length !== 1) return null
    const tkhd = child(bytes, traks[0]!, "tkhd")
    const mdia = child(bytes, traks[0]!, "mdia")
    const mdhd = mdia != null ? child(bytes, mdia, "mdhd") : null
    if (tkhd == null || mdhd == null) return null
    const tkhdBody = tkhd.start + tkhd.header
    const id = readU32(bytes, tkhdBody + (bytes[tkhdBody] === 1 ? 20 : 12))
    const mdhdBody = mdhd.start + mdhd.header
    const timescale = readU32(bytes, mdhdBody + (bytes[mdhdBody] === 1 ? 20 : 12))
    const mvex = child(bytes, moov, "mvex")
    const trex =
        mvex != null
            ? children(bytes, mvex, "trex").find((box) => readU32(bytes, box.start + box.header + 4) === id)
            : null
    const defaultDuration = trex != null ? readU32(bytes, trex.start + trex.header + 12) : 0
    return timescale > 0 ? { id, timescale, defaultDuration } : null
}

/** Un fragment : son instant de départ (`tfdt`) et sa durée (somme de ses échantillons). `null` : pas sûr. */
function fragmentOf(
    bytes: Uint8Array,
    moof: Box,
    track: { id: number; defaultDuration: number }
): { start: number | null; duration: number } | null {
    const trafs = children(bytes, moof, "traf")
    if (trafs.length !== 1) return null
    const traf = trafs[0]!
    const tfhd = child(bytes, traf, "tfhd")
    if (tfhd == null) return null
    const tfhdBody = tfhd.start + tfhd.header
    const tfhdFlags = readU32(bytes, tfhdBody) & 0xffffff
    if (readU32(bytes, tfhdBody + 4) !== track.id) return null
    // Une adresse absolue des données : l'index inséré devant la décalerait.
    if (tfhdFlags & 0x01) return null
    let at = tfhdBody + 8
    if (tfhdFlags & 0x02) at += 4
    const defaultDuration = tfhdFlags & 0x08 ? readU32(bytes, at) : track.defaultDuration

    const tfdt = child(bytes, traf, "tfdt")
    const tfdtBody = tfdt != null ? tfdt.start + tfdt.header : null
    const start =
        tfdtBody == null ? null : bytes[tfdtBody] === 1 ? readU64(bytes, tfdtBody + 4) : readU32(bytes, tfdtBody + 4)

    let duration = 0
    for (const trun of children(bytes, traf, "trun")) {
        const body = trun.start + trun.header
        const flags = readU32(bytes, body) & 0xffffff
        const count = readU32(bytes, body + 4)
        let cursor = body + 8
        if (flags & 0x01) cursor += 4
        if (flags & 0x04) cursor += 4
        const perSample = [0x100, 0x200, 0x400, 0x800].filter((flag) => flags & flag).length * 4
        if (cursor + count * perSample > trun.start + trun.size) return null
        if (flags & 0x100) {
            for (let index = 0; index < count; index++) duration += readU32(bytes, cursor + index * perSample)
        } else {
            duration += count * defaultDuration
        }
    }
    return duration > 0 ? { start, duration } : null
}

function u32(value: number): number[] {
    return [(value >>> 24) & 0xff, (value >>> 16) & 0xff, (value >>> 8) & 0xff, value & 0xff]
}

function u64(value: number): number[] {
    return [...u32(Math.floor(value / 2 ** 32)), ...u32(value % 2 ** 32)]
}

/** L'index (`sidx`, version 1) : une référence par fragment, sa taille en octets et sa durée. */
function sidxBox(
    track: { id: number; timescale: number },
    earliest: number,
    references: { size: number; duration: number }[]
): Uint8Array {
    const body = [
        1,
        0,
        0,
        0, // version 1, sans drapeaux
        ...u32(track.id),
        ...u32(track.timescale),
        ...u64(earliest),
        ...u64(0), // le premier fragment suit l'index
        0,
        0,
        (references.length >>> 8) & 0xff,
        references.length & 0xff,
        ...references.flatMap((reference) => [...u32(reference.size), ...u32(reference.duration), ...u32(SIDX_SAP)]),
    ]
    return Uint8Array.from([...u32(body.length + 8), ...[..."sidx"].map((char) => char.charCodeAt(0)), ...body])
}

/**
 * Le même vocal avec son index, ou `null` quand il n'en a pas besoin ou qu'on ne sait pas l'indexer
 * sans risque — le lecteur lit alors l'original, comme avant.
 */
export function indexedFragmentedMp4(bytes: Uint8Array): Uint8Array | null {
    const top = boxesIn(bytes, 0, bytes.length)
    if (top == null || top[0]?.type !== "ftyp") return null
    if (top.some((box) => box.type === "sidx" || box.type === "mfra")) return null
    const moov = top.find((box) => box.type === "moov")
    const moofs = top.filter((box) => box.type === "moof")
    if (moov == null || moofs.length === 0) return null
    // Le compte des références du `sidx` tient sur 16 bits : au-delà, l'index serait faux.
    if (moofs.length > 0xffff) return null
    const track = trackOf(bytes, moov)
    if (track == null) return null

    const references: { size: number; duration: number }[] = []
    let earliest: number | null = null
    for (let index = 0; index < moofs.length; index++) {
        const moof = moofs[index]!
        const fragment = fragmentOf(bytes, moof, track)
        if (fragment == null) return null
        if (index === 0) earliest = fragment.start ?? 0
        const end = moofs[index + 1]?.start ?? bytes.length
        // Une taille de référence tient sur 31 bits.
        if (end - moof.start >= 2 ** 31) return null
        references.push({ size: end - moof.start, duration: fragment.duration })
    }

    const sidx = sidxBox(track, earliest ?? 0, references)
    const insertAt = moofs[0]!.start
    const out = new Uint8Array(bytes.length + sidx.length)
    out.set(bytes.subarray(0, insertAt), 0)
    out.set(sidx, insertAt)
    out.set(bytes.subarray(insertAt), insertAt + sidx.length)
    return out
}
